<script lang="ts" setup>
// -------------------------------
// 🔹 Imports Vue & composables
// -------------------------------
import { form, isLoaderLoginFormActivated } from '../../refs/login.form.refs';
import { isCategoryActivated } from '@/refs/categories.refs';

// -------------------------------
// 🔹 Composants
// -------------------------------
import TextInput from '../common/BaseInput.vue';
import BaseButtonPrimary from '../common/BaseButtonPrimary.vue';

// -------------------------------
// 🔹 Refs & données
// -------------------------------
import { idToDelete } from '@/refs/commerce.refs';
import {
  globalHeaderTitleLayout,
  isGlobalError,
  restaurantId,
} from '@/refs/common.refs';

// -------------------------------
// 🔹 Mutations / services
// -------------------------------
import { UserMutation } from '@/mutations/user.mutation';
import { TaxeMutation } from '@/mutations/taxe.mutation';
import { form_Client_user_Body } from '@/refs/user.refs';
import {
  form_taxe_Body,
  isAtcivatedWatch,
  isAtcivatedWatchTaxe,
  isUpdateFromTaxeActivated,
} from '@/refs/taxe.refs';
import {
  categoryIds_product_promotion,
  form_promotion_Body,
  isAtcivatedWatchPromotion,
  isPromoCategoryStep,
  isPromoFistStep,
  isPromoProductStep,
  isPromoRulesStep,
  isUpdateFromPromotion,
  productIds_promotion,
  remove_categoryIds_product_promotion,
  remove_productIds_promotion,
  rules_promotion,
} from '@/refs/promotion.refs';
import ProductPage from '@/view/product/ProductPage.vue';
import CategoryPage from '@/view/category/CategoryPage.vue';
import CreateMutliRules from './createMutliRules.vue';
import { PromotionMutation } from '@/mutations/promotion.mutation';
import { toDecimal } from '@/utils/toDecimal';
import { cleanBody } from '@/utils/cleanBody';
import { haveSameElements } from '../../utils/haveSameElements';
const { handleUpdateUserCustomerById, handleDeleteUserClientCustomer } =
  UserMutation();
const { handleCreateTaxe, handleUpdateTaxById } = TaxeMutation();
const { handleCreatePromotion, handleUpdatePromotion } = PromotionMutation();
// -------------------------------
// 🔹 Émission d’événements
// -------------------------------
const emit = defineEmits<{
  (e: 'HandleClose'): void;
}>();

// -------------------------------
// 🔹 Gestion du formulaire
// -------------------------------

/**
 * Soumet le formulaire de création de commerce.
 * Gère les différentes étapes : création du commerce, ajout du contact, ou upload du logo.
 */

const handleCreate = async () => {
  isLoaderLoginFormActivated.value = true;

  const payload: any = cleanBody({
    name: form_promotion_Body.name,
    description: form_promotion_Body.description,
    type: form_promotion_Body.type,
    trigger: form_promotion_Body.trigger,
    status: form_promotion_Body.status,
    code: form_promotion_Body.code,
    value: Number(toDecimal(form_promotion_Body.value)),
    minimumOrderAmount: Number(form_promotion_Body.minimumOrderAmount),
    maximumDiscountAmount: Number(form_promotion_Body.maximumDiscountAmount),
    startDate: form_promotion_Body.startDate,
    endDate: form_promotion_Body.endDate,
    usageLimit: Number(form_promotion_Body.usageLimit),
    usageLimitPerCustomer: Number(form_promotion_Body.usageLimitPerCustomer),
    priority: Number(form_promotion_Body.priority),
    stackable: Boolean(form_promotion_Body.stackable),
    productIds: productIds_promotion.value,
    categoryIds: categoryIds_product_promotion.value,
    rules: rules_promotion.value,
    restaurantId: restaurantId.value,
  });

  try {
    const res = await handleCreatePromotion(payload);

    if (res) {
      setTimeout(() => {
        isAtcivatedWatch.value = !isAtcivatedWatch.value;
        isAtcivatedWatchPromotion.value = !isAtcivatedWatchPromotion.value;
        isLoaderLoginFormActivated.value = false;
        handleClose();
      }, 1000);
    }
  } catch (error) {
    isGlobalError.value = true;
    setTimeout(() => {
      isAtcivatedWatchPromotion.value = !isAtcivatedWatchPromotion.value;
      isLoaderLoginFormActivated.value = false;
      isGlobalError.value = false;
    }, 1000);
  }
};

const handleUpdated = async () => {
  const removeProductIds = remove_productIds_promotion.value.filter(
    (item) => !productIds_promotion.value.includes(item),
  );

  const removeCategoryIds = remove_categoryIds_product_promotion.value.filter(
    (item) => !categoryIds_product_promotion.value.includes(item),
  );

  isLoaderLoginFormActivated.value = true;
  const payload: any = cleanBody({
    name: form_promotion_Body.name,
    description: form_promotion_Body.description,
    type: form_promotion_Body.type,
    trigger: form_promotion_Body.trigger,
    status: form_promotion_Body.status,
    code: form_promotion_Body.code,
    value: Number(toDecimal(form_promotion_Body.value)),
    minimumOrderAmount: Number(form_promotion_Body.minimumOrderAmount),
    maximumDiscountAmount: Number(form_promotion_Body.maximumDiscountAmount),
    startDate: form_promotion_Body.startDate,
    endDate: form_promotion_Body.endDate,
    usageLimit: Number(form_promotion_Body.usageLimit),
    usageLimitPerCustomer: Number(form_promotion_Body.usageLimitPerCustomer),
    priority: Number(form_promotion_Body.priority),
    stackable: Boolean(form_promotion_Body.stackable),
    addProductIds:
      productIds_promotion.value.length > 0 &&
      !haveSameElements(
        productIds_promotion.value,
        remove_productIds_promotion.value,
      )
        ? productIds_promotion.value
        : null,
    removeProductIds: removeProductIds.length > 0 ? removeProductIds : null,
    addCategoryIds:
      categoryIds_product_promotion.value.length > 0 &&
      !haveSameElements(
        categoryIds_product_promotion.value,
        remove_categoryIds_product_promotion.value,
      )
        ? categoryIds_product_promotion.value
        : null,
    removeCategoryIds: removeCategoryIds.length > 0 ? removeCategoryIds : null,
  });

  try {
    const res = await handleUpdatePromotion(payload);

    if (res) {
      setTimeout(() => {
        isAtcivatedWatch.value = !isAtcivatedWatch.value;
        isAtcivatedWatchPromotion.value = !isAtcivatedWatchPromotion.value;
        isLoaderLoginFormActivated.value = false;
        handleClose();
      }, 1000);
    }
  } catch (error) {
    isGlobalError.value = true;
    setTimeout(() => {
      isAtcivatedWatchPromotion.value = !isAtcivatedWatchPromotion.value;
      isLoaderLoginFormActivated.value = false;
      isGlobalError.value = false;
    }, 1000);
  }
};

const handleSubmit = async () => {
  if (isPromoFistStep.value) {
    isPromoFistStep.value = false;
    isPromoProductStep.value = true;
    globalHeaderTitleLayout.value =
      'Associer autant de  produit que possible à cette promotion ';
  } else if (isPromoProductStep.value) {
    isPromoProductStep.value = false;
    isPromoCategoryStep.value = true;
    globalHeaderTitleLayout.value =
      'Associer autant de  catégorie que possible à cette promotion ';
  } else if (isPromoCategoryStep.value) {
    globalHeaderTitleLayout.value =
      'Associer autant de règle que possible à cette promotion ';
    isPromoCategoryStep.value = false;
    isPromoRulesStep.value = true;
  } else if (isPromoRulesStep.value) {
    if (isUpdateFromPromotion.value) {
      handleUpdated();
    } else {
      handleCreate();
    }
  }
};

/**
 * Ferme le formulaire et réinitialise l’ID à supprimer.
 */
const handleClose = (): void => {
  isPromoFistStep.value = true;
  isPromoProductStep.value = false;
  isPromoCategoryStep.value = false;
  isPromoRulesStep.value = false;
  isUpdateFromTaxeActivated.value = false;
  idToDelete.value = null;
  emit('HandleClose');
};
</script>

<template>
  <form
    autocomplete="on"
    accept-charset="UTF-8"
    @submit.prevent="handleSubmit"
    class="space-y-6"
  >
    <div v-if="isPromoProductStep">
      <ProductPage />
    </div>

    <div v-else-if="isPromoCategoryStep">
      <CategoryPage />
    </div>
    <div v-else-if="isPromoRulesStep">
      <CreateMutliRules />
    </div>
    <div class="space-y-[16px]" v-else>
      <TextInput
        required="true"
        v-model="form_promotion_Body.name"
        name="Entrez le nom de la promotion"
        placeholder="Entrez le nom de la promotion"
        type="text"
        id="Entrez le nom de la promotion"
        label="Entrez le nom de la promotion"
      />

      <TextInput
        required="true"
        v-model="form_promotion_Body.description"
        name="Entrez la description de la promotion"
        placeholder="Entrez la description de la promotion"
        type="text"
        id="Entrez la description de la promotion"
        label="Entrez la description de la promotion"
      />
      <div class="flex flex-col gap-2">
        <label
          for="taxe"
          class="text-14px font-tt-medium-Normal leading-normal"
        >
          Veuillez sélectionner le type de promotion
          <span class="text-decroissance text-14px leading-normal">*</span>
        </label>

        <select
          required
          id="taxe"
          name="taxe"
          v-model="form_promotion_Body.type"
          class="border-border-input focus:border-primary-900 focus:ring-primary-900 box-border h-(--input-height) rounded-[10px] border px-3 py-1 outline-0 focus:ring-1"
        >
          <option value="" disabled selected>
            Choisissez un type de promotion
          </option>
          <option value="percentage">Pourcentage</option>
          <option value="fixed_amount">Montant fixe</option>
          <option value="buy_one_get_one">Un acheté, un offert</option>
          <option value="quantity_discount">Quantité remise</option>
          <option value="bundle_discount">remise groupée</option>
        </select>
      </div>

      <div class="flex flex-col gap-2">
        <label
          for="taxe"
          class="text-14px font-tt-medium-Normal leading-normal"
        >
          Veuillez sélectionner un déclenchement
          <span class="text-decroissance text-14px leading-normal">*</span>
        </label>

        <select
          required
          id="taxe"
          name="taxe"
          v-model="form_promotion_Body.trigger"
          class="border-border-input focus:border-primary-900 focus:ring-primary-900 box-border h-(--input-height) rounded-[10px] border px-3 py-1 outline-0 focus:ring-1"
        >
          <option value="" disabled selected>
            Choisissez un déclenchement
          </option>
          <option value="automatic">automatique</option>
          <option value="code_required">Code obligatoire</option>
          <option value="first_time_customer">
            client pour la première fois
          </option>
          <option value="customer_segment">Segment de clientèle</option>
        </select>
      </div>

      <div class="flex flex-col gap-2">
        <label
          for="taxe"
          class="text-14px font-tt-medium-Normal leading-normal"
        >
          Veuillez sélectionner son status
          <span class="text-decroissance text-14px leading-normal">*</span>
        </label>

        <select
          required
          id="taxe"
          name="taxe"
          v-model="form_promotion_Body.status"
          class="border-border-input focus:border-primary-900 focus:ring-primary-900 box-border h-(--input-height) rounded-[10px] border px-3 py-1 outline-0 focus:ring-1"
        >
          <option value="" disabled selected>
            Choisissez un déclenchement
          </option>
          <option value="draft">brouillon</option>
          <option value="active">actif</option>
          <option value="paused">en pause</option>
          <option value="expired">expiré</option>
          <option value="exhausted">archivé/ancienne</option>
        </select>
      </div>

      <TextInput
        v-model="form_promotion_Body.code"
        name="Entrez le code promo"
        placeholder="Entrez le code promo"
        type="text"
        id="Entrez le code promo"
        label="Entrez le code promo"
      />
      <TextInput
        v-model="form_promotion_Body.value"
        name="Entrez la valeur de la promotion"
        placeholder="Entrez la valeur de la promotion"
        id="Entrez la valeur de la promotion"
        label="Entrez la valeur de la promotion"
      />

      <TextInput
        v-model="form_promotion_Body.minimumOrderAmount"
        name="Entrez la valeur de le Montant de commande minimum"
        placeholder="Entrez la valeur de le Montant de commande minimum"
        type="number"
        id="Entrez la valeur de le Montant de commande minimum"
        label="Entrez la valeur de le Montant de commande minimum"
      />

      <TextInput
        v-model="form_promotion_Body.maximumDiscountAmount"
        name="Entrez la valeur de le Montant de commande maximum"
        placeholder="Entrez la valeur de le Montant de commande maximum"
        type="number"
        id="Entrez la valeur de le Montant de commande maximum"
        label="Entrez la valeur de le Montant de commande maximum"
      />
      <TextInput
        v-model="form_promotion_Body.usageLimit"
        name="Entrez la limite d'utilisation"
        placeholder="Entrez la limite d'utilisation"
        type="number"
        id="Entrez la limite d'utilisation"
        label="Entrez la limite d'utilisation"
      />
      <TextInput
        v-model="form_promotion_Body.usageLimitPerCustomer"
        name="Entrez la limite d'utilisation par client"
        placeholder="Entrez la limite d'utilisation par client"
        type="number"
        id="Entrez la limite d'utilisation par client"
        label="Entrez la limite d'utilisation par client"
      />
      <TextInput
        v-model="form_promotion_Body.priority"
        name="Entrez la priorité"
        placeholder="Entrez la valeur prioritaire"
        type="number"
        id="Entrez la priorité"
        label="Entrez la valeur  prioritaire"
      />

      <div class="flex flex-col gap-2">
        <label
          for="taxe"
          class="text-14px font-tt-medium-Normal leading-normal"
        >
          Voulez vous que sa soit empillable ?
          <span class="text-decroissance text-14px leading-normal">*</span>
        </label>

        <select
          required
          id="taxe"
          name="taxe"
          v-model="form_promotion_Body.stackable"
          class="border-border-input focus:border-primary-900 focus:ring-primary-900 box-border h-(--input-height) rounded-[10px] border px-3 py-1 outline-0 focus:ring-1"
        >
          <option value="" disabled selected>Choisissez unr option</option>
          <option value="true">Oui</option>
          <option value="False">Non</option>
        </select>
      </div>
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label
            class="text-14px font-tt-medium-Normal mb-1 block leading-normal"
            >Date de début</label
          >
          <input
            type="datetime-local"
            v-model="form_promotion_Body.startDate"
            class="input"
          />
        </div>

        <div>
          <label
            class="text-14px font-tt-medium-Normal mb-1 block leading-normal"
            >Date de fin</label
          >
          <input
            type="datetime-local"
            v-model="form_promotion_Body.endDate"
            class="input"
          />
        </div>
      </div>
    </div>

    <div class="invisible h-[50px]" />

    <div class="fixed bottom-0 left-0 w-full bg-white py-6">
      <div class="flex flex-row items-center justify-between gap-8 px-[16px]">
        <div class="w-full">
          <BaseButtonPrimary
            type="submit"
            :isClicked="isLoaderLoginFormActivated"
            :label="
              !isPromoProductStep || !isPromoCategoryStep
                ? 'Suivant'
                : isPromoRulesStep
                  ? 'Enregistrer'
                  : 'Modifier'
            "
            style_of_btn="primary_color_of_btn "
            class="w-full text-white"
          />
        </div>

        <div class="w-full" @click="handleClose">
          <BaseButtonPrimary
            type="button"
            label="Annuler"
            style_of_btn="secondary_color_of_btn "
            class="text-grand-text w-full"
          />
        </div>
      </div>
    </div>
  </form>
</template>
